///
/// EarthBrowser
///
/// Radar legend body: the reflectivity color scale shared by the global
/// mosaic, its refinement tiles and the 3D NEXRAD volumes. Stops mirror
/// radarReflectivityPalette() in shaders/radar_common.glsl - keep the two
/// in sync.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useAppSelector } from "@/redux/store"
import { ColorScale, LegendFootnote, LegendSection, SymbolRow, gradientFromStops } from "./LegendPrimitives"

// Palette stops from radarReflectivityPalette(): [dBZ, css color]
const cPaletteStops: Array<[number, string]> = [
	[5, "rgb(0, 237, 237)"],
	[15, "rgb(0, 199, 115)"],
	[20, "rgb(0, 224, 0)"],
	[25, "rgb(0, 255, 0)"],
	[30, "rgb(255, 255, 0)"],
	[35, "rgb(232, 186, 0)"],
	[40, "rgb(255, 128, 0)"],
	[45, "rgb(255, 0, 0)"],
	[50, "rgb(204, 0, 0)"],
	[55, "rgb(255, 0, 255)"],
	[60, "rgb(153, 84, 255)"],
	[75, "rgb(255, 255, 255)"],
]

const cMinDbz = cPaletteStops[0][0]
const cMaxDbz = cPaletteStops[cPaletteStops.length - 1][0]
const cGradient = gradientFromStops(cPaletteStops)

function percentFor(dbz: number): number {
	return ((dbz - cMinDbz) / (cMaxDbz - cMinDbz)) * 100
}

const cTicks = [5, 20, 35, 50, 65].map(dbz => ({ at: percentFor(dbz), label: `${dbz}` }))

// REFS probability hatch, as drawn by overlay_radar_forecast.glsl
const cProbColor = "rgb(255, 214, 117)"
const cHatch = `repeating-linear-gradient(135deg, ${cProbColor} 0 3px, transparent 3px 8px)`

export function RadarLegendContent() {
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const volumesVisible = visibleDatasets.includes("nexrad")
	const forecastVisible = visibleDatasets.includes("radar_forecast")

	return (
		<>
			<ColorScale label="Reflectivity (dBZ)" gradient={cGradient} ticks={cTicks} />
			<div className="flex justify-between text-[10px] text-slate-500 -mt-1">
				<span>Drizzle</span>
				<span>Rain</span>
				<span>Heavy</span>
				<span>Severe / hail</span>
			</div>
			{forecastVisible && (
				<LegendSection>
					<SymbolRow swatch={<span className="h-3 w-7 rounded-sm border border-white/20" style={{ background: cGradient, opacity: 0.7 }} />}>
						Forecast echoes (lighter): RRFS 3 km model, 15-minute steps
					</SymbolRow>
					<SymbolRow swatch={<span className="h-3 w-7 rounded-sm border border-white/20" style={{ background: cHatch }} />}>
						Hatching: REFS chance of storms over 40 dBZ, denser = likelier
					</SymbolRow>
				</LegendSection>
			)}
			<LegendFootnote>
				Composite of national weather radar networks, updated every few minutes. Echoes under 5 dBZ aren't drawn; light returns are more transparent than storm cores.
				{volumesVisible && " Near NEXRAD sites (US) the 3D storm structure takes over on the same scale; the flat radar dims to a gray footprint beneath it."}
				{forecastVisible && " Past the newest scan the timeline continues into the NOAA RRFS forecast (US only, hourly runs); scrub forward to see it."}
			</LegendFootnote>
		</>
	)
}
